import { useEffect, useMemo, useState } from 'react';
import { CheckSquare, Edit, Eye, FileSpreadsheet, Loader2, Plus, Save, Search, Trash2, X } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { toast } from 'sonner';
import { trafficFineService } from '../services/trafficFineService';
import { vehicleService } from '../services/vehicleService';
import { trailerService } from '../services/trailerService';
import { documentService } from '../services/documentService';
import { driverService } from '../services/driverService';
import { staticDefinitionService } from '../services/staticDefinitionService';

type VehicleKind = 'tractor' | 'trailer';

const DEFAULT_FINE_TYPES = ['Hız', 'Park', 'Kırmızı Işık', 'Evrak', 'Geçiş İhlali', 'Diğer'];
const DEDUCTION_STATUS_OPTIONS = [
  { value: 'kesilecek', label: 'Kesilecek' },
  { value: 'kesilmeyecek', label: 'Kesilmeyecek' },
] as const;
const PROCESS_STATUS_OPTIONS = [
  { value: 'gonderilmedi', label: 'Gönderilmedi' },
  { value: 'gonderildi', label: 'Gönderildi' },
] as const;

const getProcessStatusLabel = (value?: string) => PROCESS_STATUS_OPTIONS.find((item) => item.value === value)?.label || 'Gönderilmedi';
const getDeductionStatusLabel = (value?: string) => DEDUCTION_STATUS_OPTIONS.find((item) => item.value === value)?.label || 'Kesilecek';
const calculateDeductionAmount = (fineAmount: string | number) => Number((Number(String(fineAmount || 0).replace(',', '.')) * 0.75).toFixed(2));
const getDriverSourceLabel = (value?: string) => value === 'seyir' ? 'Seyir' : value === 'manual' ? 'Manuel' : value === 'assignment' ? 'Araç Ataması' : '-';

const defaultForm = {
  vehicleKind: 'tractor' as VehicleKind,
  vehicleId: '',
  trailerId: '',
  driverId: '',
  driverName: '',
  driverPhone: '',
  driverSource: '',
  fineDate: new Date().toISOString().slice(0, 16),
  fineType: '',
  fineAmount: '',
  deductionAmount: '',
  serialNumber: '',
  deductionStatus: 'kesilecek',
  processStatus: 'gonderilmedi',
  deductionDate: '',
  description: '',
  deductionType: 'harcirah',
};

export function TrafficFines() {
  const [rows, setRows] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [trailers, setTrailers] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [deductionFilter, setDeductionFilter] = useState<'all' | 'kesilecek' | 'kesilmeyecek'>('all');
  const [processFilter, setProcessFilter] = useState<'all' | 'gonderilmedi' | 'gonderildi'>('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<any | null>(null);
  const [detailRow, setDetailRow] = useState<any | null>(null);
  const [detailDocs, setDetailDocs] = useState<any[]>([]);
  const [detailMuvafakatnameDocs, setDetailMuvafakatnameDocs] = useState<any[]>([]);
  const [form, setForm] = useState(defaultForm);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [muvafakatnameFile, setMuvafakatnameFile] = useState<File | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [bulkUpdatingStatus, setBulkUpdatingStatus] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [driverSearch, setDriverSearch] = useState('');
  const [fineTypes, setFineTypes] = useState<string[]>(DEFAULT_FINE_TYPES);
  const [installmentModalRow, setInstallmentModalRow] = useState<any | null>(null);
  const [installmentSaving, setInstallmentSaving] = useState(false);
  const [installments, setInstallments] = useState<any[]>([]);
  const [installmentForm, setInstallmentForm] = useState({ installmentCount: '3', firstInstallmentDate: new Date().toISOString().slice(0, 10), deductionType: 'harcirah' });

  const loadData = async () => {
    try {
      setLoading(true);
      const [fineRes, vehicleRes, trailerRes, driverRes, fineTypeRes] = await Promise.all([
        trafficFineService.getFines(),
        vehicleService.getVehicles(),
        trailerService.getTrailers(),
        driverService.getDrivers({ type: 'internal' }),
        staticDefinitionService.getByKey('traffic_fine_type').catch(() => ({ data: { items: DEFAULT_FINE_TYPES.map((value) => ({ value })) } })),
      ]);
      setRows(Array.isArray(fineRes?.data) ? fineRes.data : []);
      setVehicles(Array.isArray(vehicleRes?.data) ? vehicleRes.data : []);
      setTrailers(Array.isArray(trailerRes?.data) ? trailerRes.data : []);
      setDrivers(Array.isArray(driverRes?.data) ? driverRes.data : []);
      const nextFineTypes = Array.isArray((fineTypeRes as any)?.data?.items) ? (fineTypeRes as any).data.items.map((item: any) => item.value).filter(Boolean) : [];
      setFineTypes(nextFineTypes.length > 0 ? nextFineTypes : DEFAULT_FINE_TYPES);
    } catch (error: any) {
      console.error('Traffic fines load failed:', error);
      toast.error(error?.message || 'Trafik cezaları alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    setForm((current) => {
      const nextDeductionAmount = current.fineAmount === '' ? '' : String(calculateDeductionAmount(current.fineAmount));
      return current.deductionAmount === nextDeductionAmount ? current : { ...current, deductionAmount: nextDeductionAmount };
    });
  }, [form.fineAmount]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    const matchesSearch = [
      row.recordNo,
      row.vehiclePlate,
      row.trailerPlate,
      row.driverName,
      row.driverPhone,
      row.fineType,
      row.serialNumber,
    ].join(' ').toLocaleLowerCase('tr-TR').includes(search.trim().toLocaleLowerCase('tr-TR'));
    const matchesDeduction = deductionFilter === 'all' ? true : row.deductionStatus === deductionFilter;
    const matchesProcess = processFilter === 'all' ? true : row.processStatus === processFilter;
    return matchesSearch && matchesDeduction && matchesProcess;
  }), [rows, search, deductionFilter, processFilter]);

  const processSummary = useMemo(() => ({
    total: rows.length,
    gonderilmedi: rows.filter((row) => row.processStatus === 'gonderilmedi').length,
    gonderildi: rows.filter((row) => row.processStatus === 'gonderildi').length,
  }), [rows]);

  const openPreview = async (documentId?: string | null) => {
    if (!documentId) return;
    try {
      const response: any = await documentService.getDocument(documentId);
      const doc = response?.data || response;
      const rawPath = doc?.file_path || doc?.filePath || '';
      if (!rawPath) return;
      const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
      const url = rawPath.startsWith('http') ? rawPath : `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
      setPreviewImageUrl(url);
    } catch (error) {
      console.error('Traffic fine preview load failed:', error);
      toast.error('Belge açılamadı');
    }
  };

  const renderDocumentList = (documents: any[], emptyMessage: string) => {
    if (documents.length === 0) {
      return <div className="text-sm text-gray-400">{emptyMessage}</div>;
    }

    return documents.map((doc) => {
      const rawPath = String(doc.file_path || doc.filePath || '');
      const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
      const url = rawPath.startsWith('http') ? rawPath : `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;

      return (
        <div key={doc.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
          <span className="truncate text-sm text-slate-700">{doc.file_name || doc.fileName || 'Belge'}</span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPreviewImageUrl(url)} className="rounded-lg px-3 py-1 text-xs font-medium text-sky-700 hover:bg-sky-50">Önizle</button>
            <a href={url} target="_blank" rel="noreferrer" className="rounded-lg px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50">Aç</a>
          </div>
        </div>
      );
    });
  };

  const resolveAssetContext = async (vehicleKind: VehicleKind, id: string) => {
    try {
      const response: any = await trafficFineService.resolveDriverByAsset({
        vehicleKind,
        vehicleId: vehicleKind === 'tractor' ? id : undefined,
        trailerId: vehicleKind === 'trailer' ? id : undefined,
        fineDate: form.fineDate,
      });
      const data = response?.data || {};
      setForm((current) => ({
        ...current,
        vehicleId: vehicleKind === 'tractor' ? id : (data.vehicleId || current.vehicleId || ''),
        trailerId: vehicleKind === 'trailer' ? id : '',
      }));
    } catch (error) {
      console.error('Traffic fine asset resolve failed:', error);
    }
  };

  const filteredDriverOptions = drivers.filter((item) => [item.full_name || item.name, item.phone, item.cekici_plaka].join(' ').toLocaleLowerCase('tr-TR').includes(driverSearch.trim().toLocaleLowerCase('tr-TR')));

  const handleDriverSearchChange = (value: string) => {
    setDriverSearch(value);
    const match = drivers.find((item) => String(item.full_name || item.name || '').trim().toLocaleLowerCase('tr-TR') === value.trim().toLocaleLowerCase('tr-TR'));
    if (match) {
      setForm((current) => ({ ...current, driverId: String(match.id || ''), driverName: match.full_name || match.name || '', driverPhone: match.phone || '', driverSource: 'manual' }));
      return;
    }
    setForm((current) => ({ ...current, driverId: '', driverName: value, driverPhone: '', driverSource: value.trim() ? 'manual' : '' }));
  };

  const selectedAssetOptions = form.vehicleKind === 'tractor'
    ? vehicles.map((item) => ({ id: String(item.id), label: item.cekici_plaka || item.plate || item.plate_number || '-', searchText: [item.cekici_plaka, item.plate, item.plate_number].join(' ') }))
    : trailers.map((item) => ({ id: String(item.id), label: item.plate || '-', searchText: [item.plate].join(' ') }));

  const filteredAssetOptions = selectedAssetOptions.filter((item) => item.searchText.toLocaleLowerCase('tr-TR').includes(vehicleSearch.trim().toLocaleLowerCase('tr-TR')));

  const handleVehicleSearchChange = (value: string) => {
    setVehicleSearch(value);
    const match = selectedAssetOptions.find((item) => item.label.toLocaleLowerCase('tr-TR') === value.trim().toLocaleLowerCase('tr-TR'));
    if (match) {
      setForm((current) => ({
        ...current,
        vehicleId: form.vehicleKind === 'tractor' ? match.id : '',
        trailerId: form.vehicleKind === 'trailer' ? match.id : '',
        driverId: '',
        driverName: '',
        driverPhone: '',
        driverSource: '',
      }));
      setDriverSearch('');
      void resolveAssetContext(form.vehicleKind, match.id);
      return;
    }

    setForm((current) => ({
      ...current,
      vehicleId: '',
      trailerId: '',
      driverId: '',
      driverName: '',
      driverPhone: '',
      driverSource: '',
    }));
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    setForm(defaultForm);
    setImageFile(null);
    setMuvafakatnameFile(null);
    setVehicleSearch('');
  };

  const openCreate = () => {
    closeModal();
    setModalOpen(true);
  };

  const openEdit = (row: any) => {
    setEditingRow(row);
    setForm({
      vehicleKind: row.vehicleKind || 'tractor',
      vehicleId: row.vehicleId || '',
      trailerId: row.trailerId || '',
      driverId: row.driverId || '',
      driverName: row.driverName || '',
      driverPhone: row.driverPhone || '',
      driverSource: row.driverSource || '',
      fineDate: row.fineDate ? String(row.fineDate).slice(0, 16) : '',
      fineType: row.fineType || '',
      fineAmount: String(row.fineAmount || ''),
      deductionAmount: String(row.deductionAmount || ''),
      serialNumber: row.serialNumber || '',
      deductionStatus: row.deductionStatus || 'kesilecek',
      processStatus: row.processStatus || 'gonderilmedi',
      deductionDate: row.deductionDate ? String(row.deductionDate).slice(0, 16) : '',
      description: row.description || '',
      deductionType: row.deductionType || 'harcirah',
    });
    setImageFile(null);
    setMuvafakatnameFile(null);
    setVehicleSearch(row.vehicleKind === 'trailer' ? (row.trailerPlate || '') : (row.vehiclePlate || ''));
    setDriverSearch(row.driverName || '');
    setModalOpen(true);
  };

  const openDetail = async (row: any) => {
    setDetailRow(row);
    try {
      const [imageResponse, muvafakatnameResponse, installmentResponse]: any[] = await Promise.all([
        documentService.getDocuments({ entityType: 'traffic_fine', entityId: row.id, documentType: 'traffic-fine-image' }),
        documentService.getDocuments({ entityType: 'traffic_fine', entityId: row.id, documentType: 'traffic-fine-muvafakatname' }),
        trafficFineService.getInstallments(row.id).catch(() => ({ data: [] })),
      ]);
      setDetailDocs(Array.isArray(imageResponse?.data) ? imageResponse.data : []);
      setDetailMuvafakatnameDocs(Array.isArray(muvafakatnameResponse?.data) ? muvafakatnameResponse.data : []);
      setInstallments(Array.isArray(installmentResponse?.data) ? installmentResponse.data : []);
    } catch (error) {
      console.error('Traffic fine detail documents load failed:', error);
      setDetailDocs([]);
      setDetailMuvafakatnameDocs([]);
      setInstallments([]);
    }
  };

  const openInstallmentModal = (row: any) => {
    setInstallmentModalRow(row);
    setInstallmentForm({
      installmentCount: String(row.installmentCount > 0 ? row.installmentCount : '3'),
      firstInstallmentDate: new Date().toISOString().slice(0, 10),
      deductionType: row.deductionType || 'harcirah',
    });
  };

  const createInstallments = async () => {
    if (!installmentModalRow) return;
    try {
      setInstallmentSaving(true);
      const response: any = await trafficFineService.createInstallments(installmentModalRow.id, {
        installmentCount: Number(installmentForm.installmentCount || 0),
        firstInstallmentDate: installmentForm.firstInstallmentDate,
        deductionType: installmentForm.deductionType,
      });
      if (!response?.success) throw new Error(response?.message || 'Taksit planı oluşturulamadı');
      toast.success(response?.message || 'Taksit planı oluşturuldu');
      setInstallmentModalRow(null);
      if (detailRow?.id === installmentModalRow.id) {
        setInstallments(Array.isArray(response?.data) ? response.data : []);
      }
      await loadData();
    } catch (error: any) {
      console.error('Traffic fine installment create failed:', error);
      toast.error(error?.message || 'Taksit planı oluşturulamadı');
    } finally {
      setInstallmentSaving(false);
    }
  };

  const handlePayInstallment = async (installmentId: string) => {
    if (!detailRow) return;
    try {
      const response: any = await trafficFineService.payInstallment(installmentId);
      if (!response?.success) throw new Error(response?.message || 'Taksit ödendi yapılamadı');
      toast.success(response?.message || 'Taksit ödendi olarak işaretlendi');
      const installmentResponse: any = await trafficFineService.getInstallments(detailRow.id);
      setInstallments(Array.isArray(installmentResponse?.data) ? installmentResponse.data : []);
      await loadData();
    } catch (error: any) {
      console.error('Traffic fine pay installment failed:', error);
      toast.error(error?.message || 'Taksit ödendi yapılamadı');
    }
  };

  const uploadImage = async (fineId: string) => {
    if (!imageFile) return null;
    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('entityType', 'traffic_fine');
    formData.append('entityId', fineId);
    formData.append('documentType', 'traffic-fine-image');
    formData.append('name', imageFile.name);
    const upload = await documentService.uploadDocument(formData);
    return upload?.data?.id ? String(upload.data.id) : null;
  };

  const uploadMuvafakatname = async (fineId: string) => {
    if (!muvafakatnameFile) return null;
    const formData = new FormData();
    formData.append('file', muvafakatnameFile);
    formData.append('entityType', 'traffic_fine');
    formData.append('entityId', fineId);
    formData.append('documentType', 'traffic-fine-muvafakatname');
    formData.append('name', muvafakatnameFile.name);
    const upload = await documentService.uploadDocument(formData);
    return upload?.data?.id ? String(upload.data.id) : null;
  };

  const save = async () => {
    if (saving) return;
    try {
      setSaving(true);
      const payload = {
        vehicleKind: form.vehicleKind,
        vehicleId: form.vehicleId || undefined,
        trailerId: form.trailerId || undefined,
        driverId: form.driverId || undefined,
        driverName: form.driverName || undefined,
        driverPhone: form.driverPhone || undefined,
        driverSource: form.driverSource || undefined,
        fineDate: form.fineDate || undefined,
        fineType: form.fineType || undefined,
        fineAmount: Number(form.fineAmount || 0),
        deductionAmount: Number(form.deductionAmount || 0),
        serialNumber: form.serialNumber || undefined,
        deductionStatus: form.deductionStatus,
        processStatus: form.processStatus,
        deductionDate: form.deductionDate || undefined,
        description: form.description || undefined,
        imageDocumentId: editingRow?.imageDocumentId || null,
        muvafakatnameDocumentId: editingRow?.muvafakatnameDocumentId || null,
        deductionType: form.deductionType,
      };
      if (editingRow) {
        const res: any = await trafficFineService.updateFine(editingRow.id, payload);
        if (!res?.success) throw new Error(res?.message || 'Kayıt güncellenemedi');
        const [imageDocumentId, muvafakatnameDocumentId] = await Promise.all([
          uploadImage(editingRow.id),
          uploadMuvafakatname(editingRow.id),
        ]);
        if (imageDocumentId || muvafakatnameDocumentId) {
          await trafficFineService.updateFine(editingRow.id, {
            ...payload,
            imageDocumentId: imageDocumentId || payload.imageDocumentId,
            muvafakatnameDocumentId: muvafakatnameDocumentId || payload.muvafakatnameDocumentId,
          });
        }
      } else {
        const res: any = await trafficFineService.createFine(payload);
        if (!res?.success || !res?.data?.id) throw new Error(res?.message || 'Kayıt oluşturulamadı');
        const fineId = String(res.data.id);
        const [imageDocumentId, muvafakatnameDocumentId] = await Promise.all([
          uploadImage(fineId),
          uploadMuvafakatname(fineId),
        ]);
        if (imageDocumentId || muvafakatnameDocumentId) {
          await trafficFineService.updateFine(fineId, {
            ...payload,
            imageDocumentId: imageDocumentId || null,
            muvafakatnameDocumentId: muvafakatnameDocumentId || null,
          });
        }
      }
      toast.success(editingRow ? 'Trafik cezası güncellendi' : 'Trafik cezası oluşturuldu');
      closeModal();
      await loadData();
    } catch (error: any) {
      console.error('Traffic fine save failed:', error);
      toast.error(error?.message || 'Kayıt kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setSelectedRowIds((current) => current.filter((id) => rows.some((row) => row.id === id)));
  }, [rows]);

  const handleBulkProcessStatusUpdate = async (nextStatus: 'gonderildi' | 'gonderilmedi') => {
    if (selectedRowIds.length === 0) return;
    try {
      setBulkUpdatingStatus(true);
      const response: any = await trafficFineService.bulkUpdateProcessStatus(selectedRowIds, nextStatus);
      if (!response?.success) throw new Error(response?.message || 'Toplu durum güncellenemedi');
      toast.success(`Seçili cezalar ${getProcessStatusLabel(nextStatus).toLocaleLowerCase('tr-TR')} olarak güncellendi`);
      setSelectedRowIds([]);
      await loadData();
    } catch (error: any) {
      console.error('Traffic fine bulk status update failed:', error);
      toast.error(error?.message || 'Toplu durum güncellenemedi');
    } finally {
      setBulkUpdatingStatus(false);
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm(`"${row.recordNo || 'Kayıt'}" silinecek. Emin misiniz?`)) return;
    try {
      const response: any = await trafficFineService.deleteFine(row.id);
      if (!response?.success) throw new Error(response?.message || 'Silinemedi');
      toast.success(response?.message || 'Trafik cezası silindi');
      if (detailRow?.id === row.id) setDetailRow(null);
      await loadData();
    } catch (error: any) {
      console.error('Traffic fine delete failed:', error);
      toast.error(error?.message || 'Trafik cezası silinemedi');
    }
  };

  const exportExcel = () => {
    const data = filteredRows.map((row) => ({
      'Kayıt No': row.recordNo,
      'Sürücü': row.driverName,
      'Telefon': row.driverPhone,
      'Araç Tipi': row.vehicleKind === 'trailer' ? 'Dorse' : 'Çekici',
      'Çekici': row.vehiclePlate,
      'Dorse': row.trailerPlate,
      'Ceza Tarihi': row.fineDate,
      'Ceza Türü': row.fineType,
      'Ceza Tutarı': row.fineAmount,
      'Kesilecek Ceza Tutarı': row.deductionAmount,
      'Seri Sıra No': row.serialNumber,
      'Kesinti Durumu': getDeductionStatusLabel(row.deductionStatus),
      'Kesinti Tipi': row.deductionType === 'maas' ? 'Maaş' : 'Harcırah',
      'Durum': getProcessStatusLabel(row.processStatus),
      'Kesinti Tarihi': row.deductionDate,
      'Açıklama': row.description,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(data), 'Trafik_Cezalari');
    XLSX.writeFile(wb, `trafik-cezalari-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trafik Cezaları</h1>
          <p className="mt-1 text-gray-500">Araç bazlı trafik cezası kayıtlarını yönetin.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={exportExcel} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><FileSpreadsheet className="h-4 w-4" /> Excele Aktar</button>
          <button type="button" onClick={openCreate} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"><Plus className="h-5 w-5" /> Yeni Kayıt</button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        {[
          { key: 'all' as const, label: 'Toplam Ceza', value: processSummary.total, tone: 'border-slate-200 bg-white text-slate-900' },
          { key: 'gonderilmedi' as const, label: 'Gönderilmedi', value: processSummary.gonderilmedi, tone: 'border-amber-200 bg-amber-50 text-amber-900' },
          { key: 'gonderildi' as const, label: 'Gönderildi', value: processSummary.gonderildi, tone: 'border-emerald-200 bg-emerald-50 text-emerald-900' },
        ].map((card) => (
          <button
            key={card.key}
            type="button"
            onClick={() => setProcessFilter(card.key)}
            className={`rounded-2xl border p-4 text-left transition ${card.tone} ${processFilter === card.key ? 'ring-2 ring-blue-500' : 'hover:shadow-sm'}`}
          >
            <div className="text-sm font-medium opacity-80">{card.label}</div>
            <div className="mt-2 text-3xl font-bold">{card.value}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Plaka, sürücü, ceza türü ara" className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 text-sm" />
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setDeductionFilter('all')} className={`rounded-lg px-4 py-2 text-sm font-medium ${deductionFilter === 'all' ? 'bg-slate-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>Kesinti: Tümü</button>
          <button type="button" onClick={() => setDeductionFilter('kesilecek')} className={`rounded-lg px-4 py-2 text-sm font-medium ${deductionFilter === 'kesilecek' ? 'bg-slate-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>Kesilecek</button>
          <button type="button" onClick={() => setDeductionFilter('kesilmeyecek')} className={`rounded-lg px-4 py-2 text-sm font-medium ${deductionFilter === 'kesilmeyecek' ? 'bg-slate-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>Kesilmeyecek</button>
          <button type="button" onClick={() => void handleBulkProcessStatusUpdate('gonderilmedi')} disabled={selectedRowIds.length === 0 || bulkUpdatingStatus} className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-900 disabled:opacity-50">
            <CheckSquare className="h-4 w-4" /> Gönderilmedi Yap ({selectedRowIds.length})
          </button>
          <button type="button" onClick={() => void handleBulkProcessStatusUpdate('gonderildi')} disabled={selectedRowIds.length === 0 || bulkUpdatingStatus} className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-900 disabled:opacity-50">
            <CheckSquare className="h-4 w-4" /> Gönderildi Yap ({selectedRowIds.length})
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white visible-scrollbars" style={{ height: 550, overflow: 'auto' }}>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-center font-medium text-gray-500">
                  <input
                    type="checkbox"
                    checked={filteredRows.length > 0 && filteredRows.every((row) => selectedRowIds.includes(row.id))}
                    onChange={(e) => setSelectedRowIds(e.target.checked ? filteredRows.map((row) => row.id) : [])}
                    className="rounded border-gray-300"
                    aria-label="Tüm cezaları seç"
                  />
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Kayıt No</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Sürücü</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Sürücü Kaynağı</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Araç</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Ceza Türü</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Ceza Tarihi</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Ceza Tutarı</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">Kesilecek Tutar</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Kesinti Durumu</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Kesinti Tipi</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Durum</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500">Görsel</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500">Muvafakatname</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">Taksit</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={16} className="px-4 py-10 text-center text-gray-500"><Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-blue-600" />Yükleniyor...</td></tr>
              ) : filteredRows.length === 0 ? (
                <tr><td colSpan={16} className="px-4 py-10 text-center text-gray-500">Kayıt bulunamadı.</td></tr>
              ) : filteredRows.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedRowIds.includes(row.id)}
                      onChange={(e) => setSelectedRowIds((current) => e.target.checked ? [...new Set([...current, row.id])] : current.filter((id) => id !== row.id))}
                      className="rounded border-gray-300"
                      aria-label={`${row.recordNo} seç`}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-900">{row.recordNo}</td>
                  <td className="px-4 py-3">{row.driverName || '-'}<div className="text-xs text-gray-500 mt-1">{row.driverPhone || '-'}</div></td>
                  <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{getDriverSourceLabel(row.driverSource)}</span></td>
                  <td className="px-4 py-3">{row.vehicleKind === 'trailer' ? 'Dorse' : 'Çekici'} • {row.vehicleKind === 'trailer' ? row.trailerPlate : row.vehiclePlate}</td>
                  <td className="px-4 py-3">{row.fineType || '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{row.fineDate ? new Date(row.fineDate).toLocaleString('tr-TR') : '-'}</td>
                  <td className="px-4 py-3 text-right">₺{Number(row.fineAmount || 0).toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3 text-right font-semibold text-red-600">₺{Number(row.deductionAmount || 0).toLocaleString('tr-TR')}</td>
                  <td className="px-4 py-3">{getDeductionStatusLabel(row.deductionStatus)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${row.deductionType === 'maas' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>{row.deductionType === 'maas' ? 'Maaş' : 'Harcırah'}</span></td>
                  <td className="px-4 py-3">{getProcessStatusLabel(row.processStatus)}</td>
                  <td className="px-4 py-3 text-center">
                    {row.imageDocumentId ? <button type="button" onClick={() => void openPreview(row.imageDocumentId)} className="rounded-lg p-2 text-sky-600 hover:bg-sky-50"><Eye className="h-4 w-4" /></button> : <span className="text-xs text-gray-400">Yok</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {row.muvafakatnameDocumentId ? <button type="button" onClick={() => void openPreview(row.muvafakatnameDocumentId)} className="rounded-lg p-2 text-sky-600 hover:bg-sky-50"><Eye className="h-4 w-4" /></button> : <span className="text-xs text-gray-400">Yok</span>}
                  </td>
                  <td className="px-4 py-3">
                    {row.installmentCount > 0 ? (
                      <div className="text-xs text-slate-600">
                        <div>{row.installmentCount} taksit</div>
                        <div className="mt-1 text-slate-500">Sonraki: {row.nextInstallmentDate ? `${new Date(row.nextInstallmentDate).toLocaleDateString('tr-TR')} / ₺${Number(row.nextInstallmentAmount || 0).toLocaleString('tr-TR')}` : '-'}</div>
                      </div>
                    ) : <span className="text-xs text-gray-400">Plan yok</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button type="button" onClick={() => openInstallmentModal(row)} disabled={row.deductionStatus !== 'kesilecek'} className="rounded-lg px-2 py-1 text-xs font-medium text-violet-700 hover:bg-violet-50 disabled:cursor-not-allowed disabled:text-gray-300">Taksitlendir</button>
                      <button type="button" onClick={() => void openDetail(row)} className="rounded-lg p-2 text-sky-600 hover:bg-sky-50"><Eye className="h-4 w-4" /></button>
                      <button type="button" onClick={() => openEdit(row)} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50"><Edit className="h-4 w-4" /></button>
                      <button type="button" onClick={() => void handleDelete(row)} className="rounded-lg p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">{editingRow ? 'Trafik Cezası Düzenle' : 'Yeni Trafik Cezası'}</h2>
              <button type="button" onClick={closeModal} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-5 p-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Ceza Tarihi</label>
                  <input type="datetime-local" value={form.fineDate} onChange={(e) => setForm((current) => ({ ...current, fineDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Araç Tipi</label>
                  <select value={form.vehicleKind} onChange={(e) => setForm((current) => ({ ...current, vehicleKind: e.target.value as VehicleKind, vehicleId: '', trailerId: '', driverId: '', driverName: '', driverPhone: '', driverSource: '' }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    <option value="tractor">Çekici</option>
                    <option value="trailer">Dorse</option>
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Araç Bilgisi</label>
                  <input
                    list={form.vehicleKind === 'tractor' ? 'traffic-fine-vehicle-options' : 'traffic-fine-trailer-options'}
                    value={vehicleSearch}
                    onChange={(e) => handleVehicleSearchChange(e.target.value)}
                    placeholder={form.vehicleKind === 'tractor' ? 'Çekici plakası yazarak seçin' : 'Dorse plakası yazarak seçin'}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  />
                  <datalist id="traffic-fine-vehicle-options">
                    {form.vehicleKind === 'tractor' ? filteredAssetOptions.map((item) => <option key={item.id} value={item.label}>{item.label}</option>) : null}
                  </datalist>
                  <datalist id="traffic-fine-trailer-options">
                    {form.vehicleKind === 'trailer' ? filteredAssetOptions.map((item) => <option key={item.id} value={item.label}>{item.label}</option>) : null}
                  </datalist>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Sürücü</label>
                  <input list="traffic-fine-driver-options" value={driverSearch} onChange={(e) => handleDriverSearchChange(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2" placeholder="Sürücü seçin" />
                  <datalist id="traffic-fine-driver-options">
                    {filteredDriverOptions.map((item) => <option key={item.id} value={item.full_name || item.name || ''}>{item.phone || ''}</option>)}
                  </datalist>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Sürücü Telefonu</label>
                  <input value={form.driverPhone} readOnly className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Ceza Türü</label>
                  <select value={form.fineType} onChange={(e) => setForm((current) => ({ ...current, fineType: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    <option value="">Statik Liste Değeri Ekle</option>
                    {fineTypes.map((item) => <option key={item} value={item}>{item}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Ceza Tutarı</label>
                  <input type="number" step="0.01" value={form.fineAmount} onChange={(e) => setForm((current) => ({ ...current, fineAmount: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Kesilecek Ceza Tutarı</label>
                  <input type="number" step="0.01" value={form.deductionAmount} readOnly className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2" />
                  <p className="mt-1 text-xs text-gray-500">Ceza tutarının %25'i otomatik hesaplanır.</p>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Seri Sıra No</label>
                  <input value={form.serialNumber} onChange={(e) => setForm((current) => ({ ...current, serialNumber: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Kesinti Durumu</label>
                  <select value={form.deductionStatus} onChange={(e) => setForm((current) => ({ ...current, deductionStatus: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    {DEDUCTION_STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Durum</label>
                  <select value={form.processStatus} onChange={(e) => setForm((current) => ({ ...current, processStatus: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    {PROCESS_STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Kesinti Tarihi</label>
                  <input type="datetime-local" value={form.deductionDate} onChange={(e) => setForm((current) => ({ ...current, deductionDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">Kesinti Tipi</label>
                  <select value={form.deductionType} onChange={(e) => setForm((current) => ({ ...current, deductionType: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                    <option value="harcirah">Harcırah</option>
                    <option value="maas">Maaş</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Açıklama</label>
                <textarea value={form.description} onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))} rows={5} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="block cursor-pointer rounded-xl border-2 border-dashed border-slate-300 p-4 text-center text-sm text-slate-600">
                  Ceza Görseli Yükle
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
                  {imageFile ? <div className="mt-2 font-medium text-emerald-700">{imageFile.name}</div> : null}
                </label>
                <label className="block cursor-pointer rounded-xl border-2 border-dashed border-slate-300 p-4 text-center text-sm text-slate-600">
                  Muvafakatname Yükle
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setMuvafakatnameFile(e.target.files?.[0] || null)} />
                  {muvafakatnameFile ? <div className="mt-2 font-medium text-emerald-700">{muvafakatnameFile.name}</div> : null}
                </label>
              </div>

              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button type="button" onClick={closeModal} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {detailRow && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="h-full w-full max-w-2xl overflow-y-auto bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
              <h2 className="text-xl font-bold text-gray-900">Trafik Cezası Detayı</h2>
              <button type="button" onClick={() => setDetailRow(null)} className="text-gray-400 hover:text-gray-600"><X className="h-6 w-6" /></button>
            </div>
            <div className="space-y-5 p-6 text-sm">
              <div className="grid gap-4 md:grid-cols-2">
                <div><p className="text-gray-500">Kayıt No</p><p className="mt-1 font-medium text-gray-900">{detailRow.recordNo || '-'}</p></div>
                <div><p className="text-gray-500">Araç Tipi</p><p className="mt-1 font-medium text-gray-900">{detailRow.vehicleKind === 'trailer' ? 'Dorse' : 'Çekici'}</p></div>
                <div><p className="text-gray-500">Çekici</p><p className="mt-1 font-medium text-gray-900">{detailRow.vehiclePlate || '-'}</p></div>
                <div><p className="text-gray-500">Dorse</p><p className="mt-1 font-medium text-gray-900">{detailRow.trailerPlate || '-'}</p></div>
                <div><p className="text-gray-500">Sürücü</p><p className="mt-1 font-medium text-gray-900">{detailRow.driverName || '-'}</p></div>
                <div><p className="text-gray-500">Telefon</p><p className="mt-1 font-medium text-gray-900">{detailRow.driverPhone || '-'}</p></div>
                <div><p className="text-gray-500">Sürücü Kaynağı</p><p className="mt-1 font-medium text-gray-900">{getDriverSourceLabel(detailRow.driverSource)}</p></div>
                <div><p className="text-gray-500">Ceza Tarihi</p><p className="mt-1 font-medium text-gray-900">{detailRow.fineDate ? new Date(detailRow.fineDate).toLocaleString('tr-TR') : '-'}</p></div>
                <div><p className="text-gray-500">Ceza Türü</p><p className="mt-1 font-medium text-gray-900">{detailRow.fineType || '-'}</p></div>
                <div><p className="text-gray-500">Ceza Tutarı</p><p className="mt-1 font-medium text-gray-900">₺{Number(detailRow.fineAmount || 0).toLocaleString('tr-TR')}</p></div>
                <div><p className="text-gray-500">Kesilecek Tutar</p><p className="mt-1 font-medium text-gray-900">₺{Number(detailRow.deductionAmount || 0).toLocaleString('tr-TR')}</p></div>
                <div><p className="text-gray-500">Seri Sıra No</p><p className="mt-1 font-medium text-gray-900">{detailRow.serialNumber || '-'}</p></div>
                <div><p className="text-gray-500">Kesinti Durumu</p><p className="mt-1 font-medium text-gray-900">{getDeductionStatusLabel(detailRow.deductionStatus)}</p></div>
                <div><p className="text-gray-500">Durum</p><p className="mt-1 font-medium text-gray-900">{getProcessStatusLabel(detailRow.processStatus)}</p></div>
                <div><p className="text-gray-500">Kesinti Tarihi</p><p className="mt-1 font-medium text-gray-900">{detailRow.deductionDate ? new Date(detailRow.deductionDate).toLocaleString('tr-TR') : '-'}</p></div>
                <div><p className="text-gray-500">Kesinti Tipi</p><p className="mt-1 font-medium text-gray-900">{detailRow.deductionType === 'maas' ? 'Maaş' : 'Harcırah'}</p></div>
              </div>
              <div><p className="text-gray-500">Açıklama</p><div className="mt-1 rounded-xl border border-slate-200 bg-slate-50 p-4 text-gray-900">{detailRow.description || '-'}</div></div>
              <div>
                <p className="text-gray-500 mb-2">Ceza Görselleri</p>
                <div className="space-y-2">{renderDocumentList(detailDocs, 'Yüklü görsel yok.')}</div>
              </div>
              <div>
                <p className="text-gray-500 mb-2">Muvafakatname</p>
                <div className="space-y-2">{renderDocumentList(detailMuvafakatnameDocs, 'Yüklü muvafakatname yok.')}</div>
              </div>
              <div>
                <p className="text-gray-500 mb-2">Taksit Planı</p>
                <div className="space-y-2">
                  {installments.length === 0 ? <div className="text-sm text-gray-400">Taksit planı yok.</div> : installments.map((item) => (
                    <div key={item.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-700">{item.installmentNo}. taksit</span>
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${item.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                          {item.status === 'paid' ? 'Ödendi' : 'Bekliyor'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-500">{item.dueDate ? new Date(item.dueDate).toLocaleDateString('tr-TR') : '-'} / ₺{Number(item.amount || 0).toLocaleString('tr-TR')}</span>
                        {item.status === 'pending' && (
                          <button type="button" onClick={() => void handlePayInstallment(item.id)} className="rounded-lg px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50">Ödendi Yap</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {installmentModalRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-900">Ceza Taksitlendir</h2>
              <button type="button" onClick={() => setInstallmentModalRow(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4 p-6">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                <div><b>Kayıt:</b> {installmentModalRow.recordNo}</div>
                <div><b>Sürücü:</b> {installmentModalRow.driverName || '-'}</div>
                <div><b>İndirimli ceza tutarı:</b> ₺{Number(installmentModalRow.deductionAmount || 0).toLocaleString('tr-TR')}</div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Kesinti Tipi</label>
                <select value={installmentForm.deductionType} onChange={(e) => setInstallmentForm((current) => ({ ...current, deductionType: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2">
                  <option value="harcirah">Harcırah</option>
                  <option value="maas">Maaş</option>
                </select>
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Taksit Sayısı</label>
                <input type="number" min="1" step="1" value={installmentForm.installmentCount} onChange={(e) => setInstallmentForm((current) => ({ ...current, installmentCount: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              {Number(installmentForm.installmentCount) > 0 && Number(installmentModalRow.deductionAmount) > 0 && (
                <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                  Taksit başına: ₺{Number(Number(installmentModalRow.deductionAmount) / Number(installmentForm.installmentCount)).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {Number(installmentForm.installmentCount) > 0 && <div className="mt-1 text-xs text-blue-600">Son taksit kuruş farkını içerebilir.</div>}
                </div>
              )}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">İlk Taksit Tarihi</label>
                <input type="date" value={installmentForm.firstInstallmentDate} onChange={(e) => setInstallmentForm((current) => ({ ...current, firstInstallmentDate: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2" />
              </div>
              <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
                <button type="button" onClick={() => setInstallmentModalRow(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50">İptal</button>
                <button type="button" onClick={createInstallments} disabled={installmentSaving} className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-white hover:bg-violet-700 disabled:opacity-60">{installmentSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Taksit Planını Kaydet</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {previewImageUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-lg font-semibold text-gray-900">Belge Önizleme</h3>
              <button type="button" onClick={() => setPreviewImageUrl(null)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            {previewImageUrl.match(/\.pdf($|\?)/i) ? (
              <iframe src={previewImageUrl} className="h-[75vh] w-full" title="Belge Önizleme" />
            ) : (
              <img src={previewImageUrl} alt="Belge Önizleme" className="max-h-[75vh] w-full object-contain bg-white" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
